import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Space, Popconfirm, Tag, Checkbox, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UserAddOutlined } from '@ant-design/icons';
import { responsavelService, alunoService, usuarioService } from '../services/api';
import { formatCPF, onlyDigits } from '../utils/documento';

const { Option } = Select;

function ResponsaveisList() {
  const [responsaveis, setResponsaveis] = useState([]);
  const [alunos, setAlunos] = useState([]);
  const [alunosVinculados, setAlunosVinculados] = useState([]);
  const [loadingAlunosVinculados, setLoadingAlunosVinculados] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filtroNome, setFiltroNome] = useState('');
  const [filtroEmail, setFiltroEmail] = useState('');
  const [filtroCpf, setFiltroCpf] = useState('');
  const [filtroId, setFiltroId] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [vincularModalVisible, setVincularModalVisible] = useState(false);
  const [vincularUsuarioModalVisible, setVincularUsuarioModalVisible] = useState(false);
  const [editingResponsavel, setEditingResponsavel] = useState(null);
  const [vincularResponsavel, setVincularResponsavel] = useState(null);
  const [vincularUsuarioResponsavel, setVincularUsuarioResponsavel] = useState(null);
  const [usuariosResponsavel, setUsuariosResponsavel] = useState([]);
  const [form] = Form.useForm();
  const [vincularForm] = Form.useForm();
  const [vincularUsuarioForm] = Form.useForm();

  useEffect(() => {
    fetchResponsaveis(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchResponsaveis = async (page = pagination.current, pageSize = pagination.pageSize, filtros = {}) => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
      };

      const nome = (filtros.filtro_nome ?? filtroNome).trim();
      const email = (filtros.filtro_email ?? filtroEmail).trim();
      const cpf = onlyDigits(filtros.filtro_cpf ?? filtroCpf).trim();

      if (nome) params.filtro_nome = nome;
      if (email) params.filtro_email = email;
      if (cpf) params.filtro_cpf = cpf;

      const response = await responsavelService.list(params);
      const items = Array.isArray(response.data?.items) ? response.data.items : [];
      const pageInfo = response.data?.pagination || {};

      setResponsaveis(items);
      setPagination({
        current: pageInfo.page || page,
        pageSize: pageInfo.page_size || pageSize,
        total: pageInfo.total || 0,
      });
    } catch (error) {
      const detail = error.response?.data?.detail;
      message.error(detail || 'Erro ao carregar responsáveis');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlunos = async () => {
    try {
      const response = await alunoService.list();
      setAlunos(response.data);
    } catch (error) {
      console.error('Erro ao carregar alunos:', error);
    }
  };

  const fetchUsuariosResponsavel = async () => {
    try {
      const response = await usuarioService.list();
      const usuarios = Array.isArray(response.data) ? response.data : [];
      setUsuariosResponsavel(usuarios.filter((usuario) => usuario.role === 'RESPONSAVEL'));
    } catch (error) {
      console.error('Erro ao carregar usuários RESPONSAVEL:', error);
    }
  };

  const ensureAlunosLoaded = async () => {
    if (alunos.length > 0) return;
    await fetchAlunos();
  };

  const ensureUsuariosResponsavelLoaded = async () => {
    if (usuariosResponsavel.length > 0) return;
    await fetchUsuariosResponsavel();
  };

  const handleCreate = async () => {
    await ensureAlunosLoaded();
    setEditingResponsavel(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = async (record) => {
    await ensureAlunosLoaded();
    setLoading(true);
    try {
      const response = await responsavelService.get(record.id);
      const responsavelDetalhe = response.data;
      const vinculoFinanceiro = responsavelDetalhe.alunos?.find((aluno) => aluno.is_responsavel_financeiro) || responsavelDetalhe.alunos?.[0];

      setEditingResponsavel(responsavelDetalhe);
      form.setFieldsValue({
        cpf: responsavelDetalhe.cpf,
        nome_completo: responsavelDetalhe.nome_completo,
        email: responsavelDetalhe.email,
        telefone: responsavelDetalhe.telefone,
        endereco: responsavelDetalhe.endereco,
        numero: responsavelDetalhe.numero,
        complemento: responsavelDetalhe.complemento,
        bairro: responsavelDetalhe.bairro,
        cep: responsavelDetalhe.cep,
        id_aluno: vinculoFinanceiro?.id,
        tipo_vinculo: vinculoFinanceiro?.tipo_vinculo || responsavelDetalhe.tipo_cadastro || 'RESPONSAVEL',
        is_responsavel_financeiro: !!vinculoFinanceiro?.is_responsavel_financeiro,
      });
      setModalVisible(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      message.error(detail || 'Erro ao carregar detalhes do responsável');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await responsavelService.delete(id);
      message.success('Responsável excluído com sucesso');
      fetchResponsaveis();
    } catch (error) {
      const detail = error.response?.data?.detail;
      message.error(detail || 'Erro ao excluir responsável');
      console.error(error);
    }
  };

  const handleVincularAluno = async (record) => {
    await ensureAlunosLoaded();
    setVincularResponsavel(record);
    vincularForm.resetFields();
    setVincularModalVisible(true);
    await carregarAlunosVinculados(record.id);
  };

  const carregarAlunosVinculados = async (responsavelId) => {
    if (!responsavelId) {
      setAlunosVinculados([]);
      return;
    }

    setLoadingAlunosVinculados(true);
    try {
      const response = await responsavelService.listarAlunos(responsavelId);
      const vinculados = Array.isArray(response?.data?.alunos) ? response.data.alunos : [];
      setAlunosVinculados(vinculados);
    } catch (error) {
      setAlunosVinculados([]);
      const detail = error.response?.data?.detail;
      message.error(detail || 'Erro ao carregar alunos já vinculados');
      console.error(error);
    } finally {
      setLoadingAlunosVinculados(false);
    }
  };

  const handleVincularUsuario = async (record) => {
    await ensureUsuariosResponsavelLoaded();
    setVincularUsuarioResponsavel(record);
    vincularUsuarioForm.resetFields();
    setVincularUsuarioModalVisible(true);
  };

  const handleDesvincularAluno = async (responsavelId, alunoId) => {
    try {
      await responsavelService.desvincularAluno(responsavelId, alunoId);
      message.success('Aluno desvinculado com sucesso');
      fetchResponsaveis();
    } catch (error) {
      const detail = error.response?.data?.detail;
      message.error(detail || 'Erro ao desvincular aluno');
      console.error(error);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        nome_completo: values.nome_completo,
        cpf: values.cpf.replace(/\D/g, ''),
        email: values.email,
        telefone: values.telefone || null,
        endereco: values.endereco || null,
        numero: values.numero || null,
        complemento: values.complemento || null,
        bairro: values.bairro || null,
        cep: (values.cep || '').replace(/\D/g, '') || null,
        id_aluno: values.id_aluno,
        tipo_vinculo: values.tipo_vinculo,
        is_responsavel_financeiro: !!values.is_responsavel_financeiro,
      };

      if (editingResponsavel) {
        // Atualizar
        await responsavelService.update(editingResponsavel.id, payload);
        message.success('Responsável atualizado com sucesso');
      } else {
        await responsavelService.create(payload);
        message.success('Responsável cadastrado com sucesso');
      }

      setModalVisible(false);
      form.resetFields();
      fetchResponsaveis(pagination.current, pagination.pageSize);
    } catch (error) {
      const detail = error.response?.data?.detail;
      message.error(detail || 'Erro ao salvar responsável');
      console.error(error);
    }
  };

  const handleVincularModalOk = async () => {
    try {
      const values = await vincularForm.validateFields();
      await responsavelService.vincularAluno(vincularResponsavel.id, {
        id_aluno: values.id_aluno,
        tipo_vinculo: 'RESPONSAVEL',
        is_responsavel_financeiro: !!values.is_responsavel_financeiro,
      });

      message.success('Aluno vinculado com sucesso');
      setVincularModalVisible(false);
      vincularForm.resetFields();
      setAlunosVinculados([]);
      fetchResponsaveis(pagination.current, pagination.pageSize);
    } catch (error) {
      const detail = error.response?.data?.detail;
      message.error(detail || 'Erro ao vincular aluno');
      console.error(error);
    }
  };

  const handleVincularUsuarioModalOk = async () => {
    try {
      const values = await vincularUsuarioForm.validateFields();
      await responsavelService.vincularUsuario(vincularUsuarioResponsavel.id, {
        id_usuario: values.id_usuario,
      });

      message.success('Usuário vinculado com sucesso');
      setVincularUsuarioModalVisible(false);
      vincularUsuarioForm.resetFields();
      fetchResponsaveis(pagination.current, pagination.pageSize);
    } catch (error) {
      const detail = error.response?.data?.detail;
      message.error(detail || 'Erro ao vincular usuário');
      console.error(error);
    }
  };

  const statusResponsavel = (record) => {
    if (record?.ativo === true) return 'ATIVO';
    if (record?.ativo === false) return 'INATIVO';
    return 'NAO_INFORMADO';
  };

  const aplicarFiltros = () => {
    fetchResponsaveis(1, pagination.pageSize, {
      filtro_nome: filtroNome,
      filtro_email: filtroEmail,
      filtro_cpf: filtroCpf,
    });
  };

  const limparFiltros = () => {
    setFiltroId('');
    setFiltroNome('');
    setFiltroEmail('');
    setFiltroCpf('');
    fetchResponsaveis(1, pagination.pageSize, {
      filtro_nome: '',
      filtro_email: '',
      filtro_cpf: '',
    });
  };

  const responsaveisFiltrados = responsaveis.filter((responsavel) => {
    const termoId = String(filtroId || '').trim();
    if (!termoId) return true;
    const idTexto = String(responsavel?.id ?? '');
    return idTexto === termoId || idTexto.includes(termoId);
  });

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      sorter: (a, b) => (Number(a?.id) || 0) - (Number(b?.id) || 0),
    },
    {
      title: 'Nome',
      dataIndex: 'nome_completo',
      key: 'nome_completo',
      sorter: (a, b) => (a?.nome_completo || '').localeCompare(b?.nome_completo || '', 'pt-BR', { sensitivity: 'base' }),
    },
    {
      title: 'CPF',
      dataIndex: 'cpf',
      key: 'cpf',
      render: (cpf) => formatCPF(cpf),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Telefone',
      dataIndex: 'telefone',
      key: 'telefone',
    },
    {
      title: 'Usuário vinculado',
      key: 'usuario_vinculado',
      render: (_, record) => {
        if (!record.tem_usuario_vinculado) {
          return <Tag color="default">Sem usuário</Tag>;
        }
        return <Tag color="green">Usuário vinculado</Tag>;
      },
    },
    {
      title: 'Status',
      key: 'status_responsavel',
      render: (_, record) => {
        const status = statusResponsavel(record);
        if (status === 'ATIVO') return <Tag color="green">Ativo</Tag>;
        if (status === 'INATIVO') return <Tag color="red">Inativo</Tag>;
        return <Tag color="default">Não informado</Tag>;
      },
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<UserAddOutlined />}
            onClick={() => handleVincularUsuario(record)}
            title="Vincular Usuário"
          />
          <Button
            type="link"
            icon={<UserAddOutlined />}
            onClick={() => handleVincularAluno(record)}
            title="Vincular Aluno"
          />
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Tem certeza que deseja excluir este responsável?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sim"
            cancelText="Não"
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Responsáveis</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          Novo Responsável
        </Button>
      </div>

      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col xs={24} md={4}>
          <Input
            placeholder="Filtrar por ID"
            value={filtroId}
            onChange={(e) => setFiltroId(String(e?.target?.value || '').replace(/\D/g, ''))}
            onPressEnter={aplicarFiltros}
            allowClear
          />
        </Col>
        <Col xs={24} md={7}>
          <Input
            placeholder="Filtrar por nome"
            value={filtroNome}
            onChange={(e) => setFiltroNome(e.target.value)}
            onPressEnter={aplicarFiltros}
            allowClear
          />
        </Col>
        <Col xs={24} md={7}>
          <Input
            placeholder="Filtrar por e-mail"
            value={filtroEmail}
            onChange={(e) => setFiltroEmail(e.target.value)}
            onPressEnter={aplicarFiltros}
            allowClear
          />
        </Col>
        <Col xs={24} md={5}>
          <Input
            placeholder="Filtrar por CPF"
            value={formatCPF(filtroCpf)}
            onChange={(e) => setFiltroCpf(onlyDigits(e?.target?.value).slice(0, 11))}
            onPressEnter={aplicarFiltros}
            allowClear
          />
        </Col>
        <Col xs={24} md={5}>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={limparFiltros}>Limpar</Button>
            <Button type="primary" onClick={aplicarFiltros}>Buscar</Button>
          </Space>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={responsaveisFiltrados}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `Total: ${total}`,
        }}
        onChange={(nextPagination) => {
          fetchResponsaveis(nextPagination.current, nextPagination.pageSize);
        }}
      />

      {/* Modal de Criar/Editar */}
      <Modal
        title={editingResponsavel ? 'Editar Responsável' : 'Novo Responsável'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        okText="Salvar"
        cancelText="Cancelar"
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="nome_completo"
            label="Nome Completo"
            rules={[{ required: true, message: 'Nome é obrigatório' }]}
          >
            <Input placeholder="Nome do responsável" />
          </Form.Item>

          <Form.Item
            name="cpf"
            label="CPF"
            rules={[
              { required: true, message: 'CPF é obrigatório' },
              { len: 11, message: 'CPF deve ter 11 dígitos' },
            ]}
            getValueFromEvent={(e) => onlyDigits(e?.target?.value).slice(0, 11)}
            getValueProps={(value) => ({ value: formatCPF(value) })}
          >
            <Input
              placeholder="000.000.000-00"
              maxLength={14}
              inputMode="numeric"
            />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Email é obrigatório' },
              { type: 'email', message: 'Email inválido' },
            ]}
          >
            <Input placeholder="email@exemplo.com" />
          </Form.Item>

          <Form.Item name="telefone" label="Telefone">
            <Input placeholder="(00) 00000-0000" />
          </Form.Item>

          <Row gutter={12}>
            <Col span={16}>
              <Form.Item name="endereco" label="Endereco">
                <Input maxLength={200} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="numero" label="Numero">
                <Input maxLength={10} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="complemento" label="Complemento">
                <Input maxLength={100} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="bairro" label="Bairro">
                <Input maxLength={100} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="cep"
            label="CEP"
            rules={[
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const onlyDigits = String(value).replace(/\D/g, '');
                  if (onlyDigits.length !== 8) {
                    return Promise.reject(new Error('CEP deve conter 8 dígitos'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              maxLength={8}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 8);
                form.setFieldsValue({ cep: value });
              }}
            />
          </Form.Item>

          <Form.Item
            name="id_aluno"
            label="Aluno"
            rules={[{ required: true, message: 'Selecione um aluno' }]}
          >
            <Select
              placeholder="Selecione o aluno"
              showSearch
              optionFilterProp="label"
            >
              {alunos.map((aluno) => (
                <Option
                  key={aluno.id}
                  value={aluno.id}
                  label={`${aluno.nome_aluno} #${aluno.id}`}
                >
                  {aluno.nome_aluno} (ID: {aluno.id})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="tipo_vinculo"
            label="Tipo de Responsável"
            initialValue="RESPONSAVEL"
            rules={[{ required: true, message: 'Selecione o tipo de vínculo' }]}
          >
            <Select>
              <Option value="MAE">Mãe</Option>
              <Option value="PAI">Pai</Option>
              <Option value="OUTRO">Outro</Option>
              <Option value="RESPONSAVEL">Responsável</Option>
            </Select>
          </Form.Item>

          <Form.Item name="is_responsavel_financeiro" valuePropName="checked">
            <Checkbox>Responsável financeiro</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal de Vincular Alunos */}
      <Modal
        title={`Vincular Aluno - ${vincularResponsavel?.nome_completo || ''}`}
        open={vincularModalVisible}
        onOk={handleVincularModalOk}
        onCancel={() => {
          setVincularModalVisible(false);
          vincularForm.resetFields();
          setAlunosVinculados([]);
        }}
        okText="Confirmar vínculo"
        cancelText="Cancelar"
        width={500}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Alunos já vinculados</div>
          <Table
            size="small"
            loading={loadingAlunosVinculados}
            dataSource={alunosVinculados}
            rowKey="id"
            pagination={false}
            locale={{ emptyText: 'Nenhum aluno vinculado ainda' }}
            columns={[
              { title: 'Aluno', dataIndex: 'nome_aluno', key: 'nome_aluno' },
              {
                title: 'Tipo',
                dataIndex: 'tipo_vinculo',
                key: 'tipo_vinculo',
                width: 120,
              },
              {
                title: 'Financeiro',
                key: 'is_responsavel_financeiro',
                width: 110,
                render: (_, record) => (record?.is_responsavel_financeiro ? 'Sim' : 'Não'),
              },
              {
                title: 'Status',
                dataIndex: 'status_aluno',
                key: 'status_aluno',
                width: 110,
                render: (status) => status || '-',
              },
              {
                title: 'Unidade',
                key: 'id_unidades',
                width: 100,
                render: (_, record) => (record?.id_unidades ? `#${record.id_unidades}` : '-'),
              },
            ]}
          />
        </div>

        <Form form={vincularForm} layout="vertical">
          <Form.Item
            name="id_aluno"
            label="Aluno"
            rules={[{ required: true, message: 'Selecione um aluno' }]}
          >
            <Select
              placeholder="Buscar aluno por nome ou código"
              showSearch
              optionFilterProp="label"
            >
              {alunos.map((aluno) => (
                <Option
                  key={aluno.id}
                  value={aluno.id}
                  label={`${aluno.nome_aluno} #${aluno.id}`}
                >
                  {aluno.nome_aluno} (ID: {aluno.id})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="is_responsavel_financeiro" valuePropName="checked">
            <Checkbox>Responsável financeiro</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Vincular Usuário - ${vincularUsuarioResponsavel?.nome_completo || ''}`}
        open={vincularUsuarioModalVisible}
        onOk={handleVincularUsuarioModalOk}
        onCancel={() => {
          setVincularUsuarioModalVisible(false);
          vincularUsuarioForm.resetFields();
        }}
        okText="Confirmar vínculo"
        cancelText="Cancelar"
        width={500}
      >
        <Form form={vincularUsuarioForm} layout="vertical">
          <Form.Item
            name="id_usuario"
            label="Usuário RESPONSAVEL"
            rules={[{ required: true, message: 'Selecione um usuário' }]}
          >
            <Select
              placeholder="Selecione o usuário de acesso"
              showSearch
              optionFilterProp="label"
            >
              {usuariosResponsavel.map((usuario) => (
                <Option
                  key={usuario.id}
                  value={usuario.id}
                  label={`${usuario.nome_completo} (${usuario.email})`}
                >
                  {usuario.nome_completo} - {usuario.email}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default ResponsaveisList;
